import { NavLink } from 'react-router-dom';
import type { Id } from '../../api/types';
import { viewSearch } from './nav-links';
import { VIEWS } from './views';

/**
 * 🔴 THE MOBILE VIEW SWITCHER — AND THE ANSWER TO "how do you change view when there is no sidebar?"
 *
 * A bottom tab bar, not a shrunken copy of the desktop tab row, and not a hamburger drawer:
 *
 *   • On a 390×844 phone held one-handed, the top ~180px needs a regrip. The destinations someone
 *     moves between all day belong in the bottom ~90px, where the thumb already is.
 *   • A drawer costs a tap AND has to be discovered first. These are the product's entire
 *     navigation — hiding them behind a glyph to save 56px of a 844px screen is a bad trade.
 *   • Every tab is permanently visible, so "where am I and what else is there" is answered without
 *     interacting with anything.
 *
 * 🔴 IT WAS FOUR TABS UNTIL PH-26 AND IS NOW SIX — Spaces and Recurring were built, unrouted and
 * therefore in no tab bar at all. ⚠️ THE WIDTH CLAIM THIS NOTE USED TO MAKE ("≈97px per tab, room to
 * spare") WAS FOR FOUR AND IS NO LONGER TRUE, and it has NOT been re-measured in a browser: six tabs
 * over 390px is 65px each, and the longest label ("Recurring", 9 characters at 11px ≈ 53px) is an
 * ARITHMETIC estimate, not a render. ⛔ Do not quote it as measured.
 * ⇒ The tab is `min-w-0 flex-1` and the label is `truncate`, so the failure mode if the estimate is
 * wrong is an ellipsis, never a row that overflows or wraps. If Or reports a clipped label, the fix
 * is a shorter label here — not a smaller font, and not hiding a tab behind a "More".
 *
 * ⭐ Labels, not icons alone, at every width. There is no reason to make the user remember what ▦
 * means, and an icon-only tab bar is a memory test.
 *
 * ⭐ `safe-bottom` pads for the iPhone home indicator, and the row is 56px BEFORE that padding —
 * comfortably over the 44px minimum touch target. Six tabs at 65px wide × 56px tall each still clear
 * the 44px minimum on BOTH axes, which is the constraint that would actually break first.
 *
 * ⛔ `md:hidden`. From 768px up this is replaced by `ViewNav` in the top bar, next to a persistent
 * project rail. Deliberately different shapes for deliberately different hands.
 */
export function BottomTabBar({ projectId }: { projectId: Id | null }) {
  const search = viewSearch(projectId);

  return (
    <nav
      aria-label="Views"
      className="safe-bottom shrink-0 border-t border-[color:var(--color-line)] bg-[color:var(--color-surface)] md:hidden"
    >
      <ul className="flex items-stretch">
        {VIEWS.map((view) => (
          <li key={view.path} className="min-w-0 flex-1">
            <NavLink
              to={{ pathname: view.path, search }}
              className={({ isActive }) =>
                `flex h-14 flex-col items-center justify-center gap-0.5 ${
                  isActive
                    ? 'text-[color:var(--color-accent)]'
                    : 'text-[color:var(--color-ink-muted)] active:bg-black/5 dark:active:bg-white/10'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span aria-hidden="true" className="text-base leading-none">
                    {view.glyph}
                  </span>
                  <span
                    className={`truncate text-[11px] leading-none ${isActive ? 'font-bold' : 'font-medium'}`}
                  >
                    {view.label}
                  </span>
                  {/* ⭐ A colour-only "you are here" fails for a red-green reader and in bright sun.
                      The underline is a second, non-colour channel for the same fact. */}
                  <span
                    aria-hidden="true"
                    className={`mt-0.5 h-0.5 w-6 rounded-full ${
                      isActive ? 'bg-[color:var(--color-accent)]' : 'bg-transparent'
                    }`}
                  />
                </>
              )}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
